// @ts-nocheck -- recharts@2.x + @types/react@19 workspace peer conflict; tracked issue
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import type { SimulationResult } from '../engine/types'

interface Props {
  result: SimulationResult
  inflationMean: number
  startYear: number
}

function fmt(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`
  if (v >= 1_000) return `$${Math.round(v / 1_000)}k`
  return `$${Math.round(v)}`
}

/** HC-CONSTANT-CURRENCY: deflate nominal value to startYear real dollars. yearIndex is 0-based. */
function real(nominal: number, yearIndex: number, inflationMean: number): number {
  return Math.round(nominal / Math.pow(1 + inflationMean, yearIndex))
}

export function CashFlowChart({ result, inflationMean, startYear }: Props) {
  const data = result.incomeBands.map((income, i) => {
    const savings = result.savingsBands[i]
    return {
      year: income.year,
      grossIncome: real(income.p50, i, inflationMean),
      netSavings: real(savings.p50, i, inflationMean),
      netSavingsP25: real(savings.p25, i, inflationMean),
      netSavingsP75: real(savings.p75, i, inflationMean),
    }
  })

  return (
    <div className="chart-card">
      <h3>Annual Cash Flow (Median)</h3>
      <p className="chart-subtitle">
        Gross income and net savings after taxes and expenses ·{' '}
        <strong>All values in {startYear} real dollars</strong>
      </p>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={data} margin={{ top: 10, right: 20, left: 20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="year" tick={{ fontSize: 12 }} />
          <YAxis
            tickFormatter={v => fmt(v)}
            tick={{ fontSize: 12 }}
            width={70}
          />
          <Tooltip
            formatter={(v: number) => fmt(v)}
            labelFormatter={l => `Year: ${l}`}
          />
          <Legend />
          <Bar dataKey="grossIncome" fill="#60a5fa" opacity={0.7} name="Gross Income" />
          <Line
            type="monotone"
            dataKey="netSavings"
            stroke="#10b981"
            strokeWidth={2.5}
            name="Net Savings (p50)"
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="netSavingsP75"
            stroke="#34d399"
            strokeWidth={1}
            strokeDasharray="4 2"
            name="Net Savings (p75)"
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="netSavingsP25"
            stroke="#6ee7b7"
            strokeWidth={1}
            strokeDasharray="2 3"
            name="Net Savings (p25)"
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
